import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import css from './SlButton.module.scss';
import PhosphorIcon from '../PhosphorIcon';

const SlButton = ({
  children,
  variant = 'primary',
  size = 'medium',
  fullWidth = false,
  outline = false,
  icon,
  iconPosition = 'right',
  iconColor,
  to,
  href,
  disabled = false,
  onClick,
  className = '',
  manualLink = false,
  hideTextOnMobile = false,
  ...props
}) => {
  const isIconOnly = variant === 'icononly';

  const sizeMap = {
    small: 'sm',
    medium: 'md',
    large: 'lg',
    'x-large': 'xlg',
  };

  const classes = [
    css.button,
    css[`button--style-${variant}`],
    css[`button--size-${sizeMap[size]}`],
    fullWidth ? css['button--size-full-width'] : '',
    isIconOnly ? css['button--icon-only'] : '',
    hideTextOnMobile ? css ['button--hide-text-mobile'] : '',
    className,
    iconColor,
  ]
    .filter(Boolean)
    .join(' ');

  const getIconSize = () => {
    switch (size) {
      case 'small': return '1rem';
      case 'large': return '1.5rem';
      case 'x-large': return '1.75rem';
      default: return '1.25rem';
    }
  };


  const renderIcon = () => (
    <PhosphorIcon
      name={icon}
      size={getIconSize()}
      weight="bold"
      color={iconColor || 'currentColor'}
    />
  );

  const content = isIconOnly
    ? renderIcon()
    : (
      <>
        {icon && iconPosition === 'left' && renderIcon()}
        <span className={css.label}>{children}</span>
        {icon && iconPosition === 'right' && renderIcon()}
      </>
    );

  const commonProps = {
    className: classes,
    'aria-disabled': disabled,
    'aria-label': hideTextOnMobile ? children : undefined,
    onClick: disabled ? (e) => e.preventDefault() : onClick,
    ...props,
  };

  if (to) {
  return (
    <Link
      to={to}
      {...commonProps}
      onClick={disabled ? (e) => e.preventDefault() : onClick}
      aria-disabled={disabled}
    >
      {content}
    </Link>
  );
}

if (href) {
  return (
    <a
      href={href}
      {...commonProps}
      target={manualLink ? '_self' : '_blank'}
      rel="noopener noreferrer"
      onClick={disabled ? (e) => e.preventDefault() : onClick}
    >
      {content}
    </a>
  );
}

  return (
    <button
      type="button"
      disabled={disabled}
      {...commonProps}
    >
      {content}
    </button>
  );
};

SlButton.propTypes = {
  children: PropTypes.node,
  variant: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium', 'large', 'x-large']),
  fullWidth: PropTypes.bool,
  outline: PropTypes.bool,
  icon: PropTypes.string,
  iconPosition: PropTypes.oneOf(['left', 'right']),
  iconColor: PropTypes.string,
  href: PropTypes.string,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string,
  manualLink: PropTypes.bool,
  to: PropTypes.string,
  hideTextOnMobile: PropTypes.bool,
};

export default SlButton;
